import { useEffect, useMemo, useState } from "react";

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

interface EventDatePickerProps {
  availableDates: string[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
  peakDate?: string;
}

function ymd(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function monthLabel(year: number, month: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function nearestAvailable(wanted: string, available: string[]): string {
  if (available.includes(wanted)) return wanted;
  let best = available[0];
  let bestDist = Infinity;
  const target = Date.parse(wanted);
  for (const date of available) {
    const dist = Math.abs(Date.parse(date) - target);
    if (dist < bestDist) {
      best = date;
      bestDist = dist;
    }
  }
  return best ?? wanted;
}

export function EventDatePicker({
  availableDates,
  selectedDate,
  onSelectDate,
  peakDate,
}: EventDatePickerProps) {
  const months = useMemo(() => {
    const seen = new Set<string>();
    const list: { year: number; month: number }[] = [];
    for (const date of availableDates) {
      const key = date.slice(0, 7);
      if (seen.has(key)) continue;
      seen.add(key);
      const [year, month] = key.split("-").map(Number);
      list.push({ year, month });
    }
    return list;
  }, [availableDates]);

  const selectedKey = selectedDate.slice(0, 7);
  const [viewKey, setViewKey] = useState(selectedKey);

  useEffect(() => {
    setViewKey(selectedDate.slice(0, 7));
  }, [selectedDate]);

  const viewIndex = Math.max(
    0,
    months.findIndex((m) => `${m.year}-${String(m.month).padStart(2, "0")}` === viewKey),
  );
  const view = months[viewIndex] ?? months[0];
  if (!view) return null;

  const first = availableDates[0];
  const last = availableDates[availableDates.length - 1];
  const available = new Set(availableDates);
  const days = new Date(Date.UTC(view.year, view.month, 0)).getUTCDate();
  const startPad = (new Date(Date.UTC(view.year, view.month - 1, 1)).getUTCDay() + 6) % 7;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-faint">
          Date
        </p>
        <label className="sr-only" htmlFor="explorer-date">
          Jump to date
        </label>
        <input
          id="explorer-date"
          type="date"
          min={first}
          max={last}
          value={selectedDate}
          onChange={(e) => onSelectDate(nearestAvailable(e.target.value, availableDates))}
          className="w-full min-w-0 rounded-lg border border-line/35 bg-surface px-2 py-1.5 text-xs text-ink outline-none focus:border-accent sm:w-auto"
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          disabled={viewIndex <= 0}
          onClick={() => {
            const prev = months[viewIndex - 1];
            if (prev) setViewKey(`${prev.year}-${String(prev.month).padStart(2, "0")}`);
          }}
          className="rounded-md px-2 py-1 text-sm text-muted disabled:opacity-30 hover:text-ink"
          aria-label="Previous month"
        >
          ‹
        </button>
        <p className="text-sm font-semibold text-ink">{monthLabel(view.year, view.month)}</p>
        <button
          type="button"
          disabled={viewIndex >= months.length - 1}
          onClick={() => {
            const next = months[viewIndex + 1];
            if (next) setViewKey(`${next.year}-${String(next.month).padStart(2, "0")}`);
          }}
          className="rounded-md px-2 py-1 text-sm text-muted disabled:opacity-30 hover:text-ink"
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="mt-2 grid grid-cols-7 gap-1 text-center text-[11px] text-faint">
        {WEEKDAYS.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {Array.from({ length: startPad }, (_, i) => (
          <span key={`pad-${i}`} />
        ))}
        {Array.from({ length: days }, (_, i) => {
          const date = ymd(view.year, view.month, i + 1);
          const enabled = available.has(date);
          const selected = date === selectedDate;
          const peak = date === peakDate;
          return (
            <button
              key={date}
              type="button"
              disabled={!enabled}
              onClick={() => onSelectDate(date)}
              title={peak ? "Peak day" : undefined}
              className={[
                "h-9 rounded-md text-xs font-medium transition sm:h-8",
                selected
                  ? "bg-accent text-[#0a100e]"
                  : peak
                    ? "border border-accent text-accent"
                    : enabled
                      ? "text-ink hover:bg-accent/15"
                      : "cursor-not-allowed text-faint/35",
              ].join(" ")}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-faint">
        Only mapped days in this event are selectable.
      </p>
    </div>
  );
}
